import { Controller, useFormContext } from 'react-hook-form';
import { PaginatedSelectContent } from './PaginatedSelectContent';
import { PaginatedSelectProvider } from './PaginatedSelectContext';
import { PaginatedSelectTrigger } from './PaginatedSelectTrigger';
import { usePaginatedSelect } from './paginated-select.hooks';
import type { PaginatedSelectProps } from './paginated-select.types';
import { cn } from '@/shared/lib/cn';

function PaginatedSelect<T extends { id: string }>({
  mode = 'single',
  enableSearch = false,
  fetchFn,
  pageSize = 10,
  value: externalValue,
  onChange: externalOnChange,
  disabled = false,
  renderItem: customRenderItem,
  error,
  name,
  control: externalControl,
  rules,
  label,
  placeholder,
  helperText,
  className,
}: PaginatedSelectProps<T>) {
  const formContext = useFormContext?.();
  const internalControl = formContext?.control;
  const control = externalControl || internalControl;

  const { contextValue } = usePaginatedSelect<T>({
    mode,
    enableSearch,
    fetchFn,
    pageSize,
    value: externalValue,
    onChange: externalOnChange,
    disabled,
    renderItem: customRenderItem,
    error,
    name,
    control,
    rules,
  });

  const mergedContext = {
    ...contextValue,
    label,
    placeholder,
    helperText,
    error: error || contextValue.error,
  };

  if (name && control) {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <PaginatedSelectProvider
            value={{
              ...mergedContext,
              value: field.value ?? (mode === 'multiple' ? [] : ''),
              onValueChange: (val) => {
                field.onChange(val);
              },
            }}
          >
            <div className={cn('relative', className)}>
              <PaginatedSelectTrigger />
              <PaginatedSelectContent className="absolute top-full left-0 right-0 mt-1 z-50" />
            </div>
          </PaginatedSelectProvider>
        )}
      />
    );
  }

  return (
    <PaginatedSelectProvider value={mergedContext}>
      <div className={cn('relative', className)}>
        <PaginatedSelectTrigger />
        <PaginatedSelectContent className="absolute top-full left-0 right-0 mt-1 z-50" />
      </div>
    </PaginatedSelectProvider>
  );
}

export { PaginatedSelect };
